import styles from './LegalPage.module.css';

const PrivacyPage = () => {
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <h1>Privacy Policy</h1>
        <p className={styles.lastUpdated}>Last Updated: April 2, 2026</p>

        <section>
          <p>
            This Privacy Policy ("Policy") describes how Oval Care, Inc. ("Oval Care," "Company," "we," "our," or "us") collects, uses, discloses, and protects your information when you use our website located at <a href="https://ovalcare.com">https://ovalcare.com</a> and any related pages, applications, or services (collectively, the "Site"). By using the Site, you agree to this Policy and to our <a href="/terms">Terms of Use</a>. If you do not agree, please do not use our Site.
          </p>
        </section>

        <section>
          <h2>1. About the Site</h2>
          <p>
            Oval Care operates as a <strong>technology platform</strong> that facilitates connections between users and independent, licensed healthcare providers. Oval Care is not a healthcare provider, medical practice, or pharmacy. We do not provide medical advice, diagnoses, treatments, or prescriptions. The Site helps users access health-related information, connect with independent healthcare providers, and manage prescriptions or wellness services through our platform. In doing so, Oval Care may collect information about you to:
          </p>
          <ul>
            <li>Provide information about your medications and healthcare services;</li>
            <li>Display prices and service options from participating partners;</li>
            <li>Send reminders, confirmations, or communications related to your account or care;</li>
            <li>Deliver marketing messages and promotions where legally permitted.</li>
          </ul>
          <p>
            Oval Care does not control, direct, or influence any medical decisions, diagnoses, treatments, or prescriptions. All such decisions are made solely by independent licensed healthcare providers. Oval Care's role is limited to facilitating access to the platform and transmitting information between users and providers.
          </p>
        </section>

        <section>
          <h2>2. Categories of Information We Collect</h2>
          <p>
            Depending on your interaction with the Site, we may collect the following types of information:
          </p>

          <h3>Information You Provide Directly:</h3>
          <ul>
            <li>Personal identifiers (name, address, phone number, email address);</li>
            <li>Medical or prescription information you provide (e.g., medication names and dosage);</li>
            <li>Payment details and transaction information;</li>
            <li>Geographic information such as ZIP code or city;</li>
            <li>User preferences and account settings.</li>
          </ul>

          <h3>Information We Collect Automatically:</h3>
          <ul>
            <li>Device and browser information (IP address, operating system, model, device identifier);</li>
            <li>Usage data (pages visited, actions taken, time spent on Site);</li>
            <li>Cookies and analytics data (see Section 13 below).</li>
          </ul>

          <h3>Information from Third Parties:</h3>
          <ul>
            <li>We may receive limited information from service providers, pharmacies, or healthcare professionals assisting with your care.</li>
          </ul>
        </section>

        <section>
          <h2>3. How We Use Information</h2>
          <ul>
            <li>To operate and maintain the Site and related services;</li>
            <li>To process transactions, prescriptions, and customer requests;</li>
            <li>To personalize your experience based on your preferences and location;</li>
            <li>To send administrative and service-related notifications (including SMS or email);</li>
            <li>To deliver marketing or promotional materials (you can opt out at any time);</li>
            <li>To improve our products, Site, and user experience;</li>
            <li>To detect and prevent fraud, abuse, or other prohibited conduct;</li>
            <li>To comply with legal obligations and enforce our Terms of Use.</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Share Information</h2>

          <p><strong>With Pharmacies and Providers:</strong> To fulfill prescriptions or coordinate care.</p>

          <p><strong>With Service Providers:</strong> To operate our business and Site (e.g., hosting, data storage, analytics, customer service).</p>

          <p><strong>With Business Partners:</strong> For joint marketing or service delivery, when permitted by law.</p>

          <p><strong>For Legal or Safety Reasons:</strong> To comply with legal obligations, protect rights, or ensure safety.</p>

          <p><strong>In the Event of a Business Transfer:</strong> In connection with a merger, acquisition, or sale of assets.</p>

          <p>
            We may also share aggregated or de-identified information that does not identify you personally.
          </p>
        </section>

        <section>
          <h2>5. Healthcare Data Sharing</h2>
          <p>
            Because Oval Care facilitates connections between you and independent healthcare providers, certain health-related information you provide may be shared in the following circumstances:
          </p>
          <ul>
            <li><strong>Independent Healthcare Providers:</strong> Your health information, including medical history, symptoms, and intake form responses, may be shared with licensed, independent healthcare providers to facilitate telehealth consultations, medical evaluations, and prescription decisions. These providers are not employees or agents of Oval Care.</li>
            <li><strong>Licensed Pharmacies:</strong> Prescription information and relevant personal details (such as name, address, and date of birth) may be shared with licensed pharmacies for the purpose of prescription fulfillment and delivery.</li>
            <li><strong>As Required by Law:</strong> We may disclose your health information when required to do so by applicable federal, state, or local law, including in response to court orders, subpoenas, or government investigations.</li>
          </ul>
          <p>
            Oval Care takes reasonable measures to ensure that healthcare providers and pharmacies receiving your information maintain appropriate safeguards. However, once your information is shared with independent providers or pharmacies, their use of that information is governed by their own privacy practices and applicable regulations.
          </p>
          <p>
            Once your information is shared with independent healthcare providers or pharmacies, those entities are solely responsible for their use, storage, and protection of your information, as well as for any medical care, prescriptions, or services they provide. Oval Care is not responsible or liable for any actions or omissions of such third parties.
          </p>
        </section>

        <section>
          <h2>6. No Medical Advice</h2>
          <p>
            The information provided on the Site, including health-related content, articles, product descriptions, and any other materials, is for <strong>informational purposes only</strong> and does not constitute medical advice, diagnosis, or treatment. Nothing on the Site should be interpreted as a substitute for professional medical advice from a licensed healthcare provider.
          </p>
          <p>
            You should always consult with a qualified healthcare professional before making any decisions about your health, medications, or treatment plans. Oval Care does not recommend or endorse any specific tests, providers, products, procedures, or opinions that may appear on the Site.
          </p>
          <p>
            If you are experiencing a medical emergency, call 911 or your local emergency services immediately. Do not rely on the Site for emergency medical guidance.
          </p>
        </section>

        <section>
          <h2>7. User Responsibility</h2>
          <p>
            You are solely responsible for the accuracy, completeness, and truthfulness of all information you provide through the Site, including but not limited to:
          </p>
          <ul>
            <li>Medical history and current health conditions;</li>
            <li>Current medications, supplements, and allergies;</li>
            <li>Personal identification and contact information;</li>
            <li>Insurance and payment information.</li>
          </ul>
          <p>
            Inaccurate, incomplete, or misleading information may result in inappropriate medical recommendations, delayed care, or safety risks. Oval Care and its independent healthcare providers rely on the information you provide to facilitate consultations and prescriptions. You agree that Oval Care shall not be held liable for any adverse outcomes resulting from inaccurate or incomplete information provided by you.
          </p>
        </section>

        <section>
          <h2>8. Information Security</h2>
          <p>
            Oval Care uses reasonable technical, administrative, and physical safeguards to protect your personal information, including SSL encryption for data transmitted through secure areas of our Site. However, no method of transmission over the Internet or storage system is completely secure. By using the Site, you acknowledge that transmission of data is at your own risk.
          </p>
          <p>
            Emails and other communications you send to us may not be encrypted. Please avoid sending sensitive information via unencrypted email.
          </p>
        </section>

        <section>
          <h2>9. Your Choices</h2>
          <p>
            You may opt out of marketing emails by clicking "Unsubscribe" in our messages or contacting <a href="mailto:support@ovalcare.com">support@ovalcare.com</a>. Some administrative communications (such as service notifications or security alerts) are required and cannot be opted out of.
          </p>
          <p>
            The Site does not currently respond to "Do Not Track" signals. For more information about this setting, visit <a href="https://www.allaboutdnt.org" target="_blank" rel="noopener noreferrer">www.allaboutdnt.org</a>.
          </p>
        </section>

        <section>
          <h2>10. Information About Minors</h2>
          <p>
            Oval Care's Site and Services are intended for adults and are not directed to individuals under the age of 18. We do not knowingly collect information from minors. If you believe your child has provided us personal data, please contact <a href="mailto:privacy@ovalcare.com">privacy@ovalcare.com</a> so we can delete it.
          </p>
        </section>

        <section>
          <h2>11. California Residents</h2>
          <p>
            If you are a California resident, you may have rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA). Please review our California Privacy Notice for additional information about your rights and how to exercise them.
          </p>
        </section>

        <section>
          <h2>12. Links to Other Websites</h2>
          <p>
            Our Site may contain links to third-party websites or services that are not operated by Oval Care. We are not responsible for their privacy practices or content. We encourage you to review the privacy policies of any website you visit.
          </p>
        </section>

        <section>
          <h2>13. Cookies and Similar Technologies</h2>
          <p>
            We use cookies and similar tracking technologies to:
          </p>
          <ul>
            <li>Enable essential Site functionality;</li>
            <li>Analyze usage patterns and improve performance;</li>
            <li>Personalize your experience and deliver relevant content.</li>
          </ul>
          <p>
            You can disable cookies through your browser settings, but some Site functions may not operate properly. By continuing to use our Site, you consent to our use of cookies.
          </p>
          <p>
            We use Google Analytics to analyze Site activity. Learn more about how Google uses information here: <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">https://policies.google.com/technologies/partner-sites</a>.
          </p>
        </section>

        <section>
          <h2>14. Retention</h2>
          <p>
            We retain personal information for as long as necessary to fulfill the purposes described in this Policy, comply with legal obligations, resolve disputes, and enforce agreements. You may request deletion of your data at any time by contacting <a href="mailto:support@ovalcare.com">support@ovalcare.com</a>.
          </p>
        </section>

        <section>
          <h2>15. Access from Outside the United States</h2>
          <p>
            If you access the Site from outside the United States, please note that your information will be transferred to and processed in the United States, where privacy laws may differ from those in your jurisdiction.
          </p>
        </section>

        <section>
          <h2>16. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically. When we do, we will revise the "Last Updated" date above. Your continued use of the Site after changes have been posted constitutes your acceptance of the updated Policy.
          </p>
        </section>

        <section>
          <h2>17. Contact Us</h2>
          <p>
            If you have any questions, please contact us at:
          </p>
          <address>
            Oval Care, Inc.<br />
            Attn: Patient Care Team<br />
            848 Brickell Avenue PH5,<br />
            Miami, FL 33131<br />
            United States<br />
            Phone: +1 (737) 396‑1791<br />
            Email: <a href="mailto:support@ovalcare.com">support@ovalcare.com</a>
          </address>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPage;
